import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '1/2 Truck',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(false);

    try {
      const body = new URLSearchParams();
      body.append('name', formData.name);
      body.append('email', formData.email);
      body.append('phone', formData.phone);
      body.append('service', formData.service);
      body.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d66gjckkan35qnu5ing0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: body.toString(),
      });

      if (response.ok) {
        setSubmitSuccess(true);
        setFormData({
          name: '',
          email: '',
          phone: '',
          service: '1/2 Truck',
          message: ''
        });
        setTimeout(() => {
          setSubmitSuccess(false);
        }, 5000);
      } else {
        setSubmitError(true);
      }
    } catch {
      setSubmitError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contact" className="py-24 bg-[#F9FAFB]">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Left Side - Contact Info */}
          <div>
            <h2 className="text-5xl font-serif text-[#2C2C2C] mb-6">Get in Touch</h2>
            <p className="text-lg text-[#6B7280] mb-12">
              Ready to clear your space? Contact us today for a free estimate. We're here to help make your junk removal experience easy and stress-free.
            </p>

            {/* Contact Details */}
            <div className="space-y-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#FF6B4A] rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-phone-fill text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-[#2C2C2C] mb-1">Phone</h3>
                  <a href="tel:+15134489448" className="text-[#6B7280] hover:text-[#FF6B4A] transition-colors">
                    (513) 448-9448
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#FF6B4A] rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-fill text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-[#2C2C2C] mb-1">Email</h3>
                  <a href="mailto:charlestonjunkremovers@gmail.com" className="text-[#6B7280] hover:text-[#FF6B4A] transition-colors">
                    charlestonjunkremovers@gmail.com
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#FF6B4A] rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-pin-fill text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-[#2C2C2C] mb-1">Service Area</h3>
                  <p className="text-[#6B7280]">
                    Charleston, SC and surrounding areas
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#FF6B4A] rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-time-fill text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-[#2C2C2C] mb-1">Hours</h3>
                  <p className="text-[#6B7280]">
                    Monday - Saturday: 8:00 AM - 7:00 PM
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Contact Form */}
          <div className="bg-white rounded-3xl shadow-xl p-8 lg:p-10">
            <h3 className="text-2xl font-bold text-[#2C2C2C] mb-6">Request a Free Estimate</h3>
            
            {submitSuccess && (
              <div className="mb-6 bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg">
                <p className="text-sm font-medium">Thank you! We'll contact you shortly.</p>
              </div>
            )}

            {submitError && (
              <div className="mb-6 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
                <p className="text-sm font-medium">Something went wrong. Please try again or call us directly.</p>
              </div>
            )}

            <form id="contact-estimate-form" data-readdy-form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-[#2C2C2C] mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6B4A] text-sm"
                  placeholder="John Smith"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-[#2C2C2C] mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6B4A] text-sm"
                  placeholder="john@example.com"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-[#2C2C2C] mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6B4A] text-sm"
                  placeholder="(513) 448-9448"
                />
              </div>

              <div>
                <label htmlFor="service" className="block text-sm font-semibold text-[#2C2C2C] mb-2">
                  Service Needed
                </label>
                <select
                  id="service"
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6B4A] text-sm cursor-pointer"
                >
                  <option value="1/4 Truck - $150">1/4 Truck - $150</option>
                  <option value="1/2 Truck - $275">1/2 Truck - $275</option>
                  <option value="3/4 Truck - $400">3/4 Truck - $400</option>
                  <option value="Full Truck - $550">Full Truck - $550</option>
                  <option value="Not Sure - Need Estimate">Not Sure - Need Estimate</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-[#2C2C2C] mb-2">
                  Additional Details
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6B4A] text-sm resize-none"
                  placeholder="Tell us about your junk removal needs..."
                ></textarea>
                <p className="text-xs text-[#9CA3AF] mt-1 text-right">{formData.message.length}/500</p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#FF6B4A] text-white px-8 py-4 rounded-full hover:bg-[#ff5533] transition-all whitespace-nowrap text-base font-semibold disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              >
                {isSubmitting ? 'Sending...' : 'Get Free Estimate'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
